// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { LytEventCTO } from '@shared/lytEventCTO'

import { TransactionCTO, TransactionStatus } from '@shared/TransactionCTO'
import axios from 'axios'

import { fetchChainTimestampDelta } from '@react/chainThunks/fetchChainTimestampDeltaTx'
import { fetchDailyPoolOverviewCTOs } from '@react/fetchers/PoolFetchers'
import { LytReservesCTOAllocation } from '@shared/LytReservesCTO'
import { ReserveActionPlanStateDataCTO } from '@shared/ReservePlanCTO'
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { timestampToUTCDateString } from '@shared/conversions'

import { logOT } from '@shared/loggingShared'
import { formatAgeFromTimestamp } from '@shared/sharedUtils'
import { JsonRpcProvider } from 'ethers'
import { User } from 'oidc-client-ts'
import { LytExchangeRateCheckCTO } from '@shared/LytExchangeRateCheckCTO'
import type { ActivitiesResponse } from '@shared/activitiesTypes'

export async function convertPendingTxToLytEvent(tx: TransactionCTO, timeDelta: number): Promise<LytEventCTO> {
  // Pick the correct timestamp based on status
  let validTimestamp: number | undefined
  if (tx.status === TransactionStatus.Presigned) {
    validTimestamp = tx.presignedTimestamp
  } else if (tx.status === TransactionStatus.Signed) {
    validTimestamp = tx.signedTimestamp
  }

  if (!validTimestamp || isNaN(validTimestamp) || validTimestamp <= 0) {
    logOT.info('STILL NOT WORKING')
    validTimestamp = Date.now() // Use current time as fallback
  }

  // Use shared utility for age formatting
  const ageString = formatAgeFromTimestamp(validTimestamp, timeDelta)

  // Use less precise date format for consistency
  let ts = validTimestamp
  if (ts > 1e12) {
    ts = Math.floor(ts / 1000)
  }
  const timestampDateString = timestampToUTCDateString(ts)

  return {
    id: tx.id,
    poolAddr: tx.address, // TransactionCTO.address maps to LytEventCTO.poolAddr
    fromAddr: tx.sender,
    toAddr: tx.address || 'Pending', //HERE: Use pool address for pending transactions, or "Pending..." as fallback
    accountAddr: tx.sender, // Use sender as the account address for pending transactions
    poolName: '', // Not available in TransactionCTO, use empty string

    eventType: tx.eventType,
    eventName: tx.eventName,
    currency: 'USD', // Default value since not in TransactionCTO
    symbol: 'MUSDC', // Default value since not in TransactionCTO

    blockNumber: 0, // Not available in TransactionCTO, use 0
    transactionHash: (tx.transactionHash ||
      '0x0000000000000000000000000000000000000000000000000000000000000000') as TxHash,
    ageString,
    timestamp: validTimestamp,
    timestampDateString, // Use consistent format
    timestampString: new Date(validTimestamp).toISOString(),
    liquidityTokenSymbol: 'MUSDC', // Default value

    chainId: tx.chainId,

    // Handle different transaction types
    assets:
      tx.methodName === 'deposit'
        ? ((tx.args?.[0]?.toString() || '0') as `${number}`) // For mints, assets (USDC) is in args[0]
        : ('0' as `${number}`), // For redemption requests, assets are 0 until processed
    shares:
      tx.methodName === 'requestRedeem'
        ? ((tx.args?.[0]?.toString() || '0') as `${number}`) // For redeems, shares is in args[0]
        : ('0' as `${number}`), // For mints, shares are 0 until processed
    fees: '0' as `${number}`,
  }
}

/* spellchecker: disable */

export const swapConfirmationFunction = (): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, 2000)
  })
}

export const fetchLytEvents = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  params: { vaultAddress: string; walletAddress?: string; startDayStr?: string; endDayStr?: string },
  providerReadOnly: JsonRpcProvider
): Promise<LytEventCTO[]> => {
  // Build the endpoint path
  let path = `${serverApiBaseUrl}/lytEvents/${params.vaultAddress}`
  if (params.walletAddress) {
    path += `/${params.walletAddress}`
  }

  // Build query string
  const query = []
  if (params.startDayStr) query.push(`startDayStr=${encodeURIComponent(params.startDayStr)}`)
  if (params.endDayStr) query.push(`endDayStr=${encodeURIComponent(params.endDayStr)}`)
  const queryString = query.length ? `?${query.join('&')}` : ''

  // Make the request
  const response = await axios.get(`${path}${queryString}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })

  const events = response.data.lytEvents

  // Try to get latestBlockTimestamp from backend response, fallback to system time
  const localStorageKey = 'processingTransaction'
  const pendingTxString = localStorage.getItem(localStorageKey)
  const pendingTx = pendingTxString ? JSON.parse(pendingTxString) : undefined

  const filteredEvents = events.filter((event: LytEventCTO) => {
    if (!event) return false

    const isLegitimateEventType = [
      'Mint',
      'RedemptionRequested',
      'Redeemed',
      'Redeem',
      'PoolDeposit',
      'Deposit',
      'Withdraw',
      'RedeemRepay',
    ].includes(event.eventType || event.eventName || '')

    return isLegitimateEventType || event.fromAddr !== '0x0000000000000000000000000000000000000000'
  })

  if (
    pendingTx &&
    (pendingTx.status === TransactionStatus.Presigned || pendingTx.status === TransactionStatus.Signed)
  ) {
    logOT.info('STATUS:', pendingTx.status)
    const timeDelta = await fetchChainTimestampDelta(providerReadOnly)
    const pendingEvent = await convertPendingTxToLytEvent(pendingTx, timeDelta)

    // Check if there's already a mined event with the same transaction hash
    const hasMinedEvent = filteredEvents.some(
      (event: LytEventCTO) => event.transactionHash === pendingEvent.transactionHash
    )

    if (!hasMinedEvent) return [pendingEvent, ...filteredEvents]
  }
  return filteredEvents
}
export const lytPoolReservePut = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  lytReservesCTOAllocation: LytReservesCTOAllocation,
  cognitoUser?: User
): Promise<LytReservesCTOAllocation> => {
  const accessToken = cognitoUser?.access_token
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    'x-api-key': restAPIKey,
    'Content-Type': 'application/json', // Assuming you're sending JSON data
  }

  logOT.info(lytPoolReservePut.name, serverApiBaseUrl, headers)
  const r = await axios.put(`${serverApiBaseUrl}/lytPoolReserve`, lytReservesCTOAllocation, {
    headers,
  })
  const data = r.data

  return data.results
}
export const fetchLytPoolReserve = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  lytPoolAddr: EthAddr
): Promise<LytReservesCTOAllocation> => {
  try {
    const r = await axios.get(`${serverApiBaseUrl}/lytPoolReserve/${lytPoolAddr}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    const data = r.data
    if (!data.result) {
      return {
        lytPoolAddr,
        timestamp: 1,
        reserves: [],
      }
    }

    const lytPoolReserveFetchResult = data.result
    return lytPoolReserveFetchResult
  } catch (e) {
    logOT.error('fetchLytPoolReserve error', e)
    throw e
  }
}

export interface LytHistoryCTO {
  timestamp: number
  value: number
}

// Points API Types
export interface PointsLedgerEntry {
  address: string
  pointsThisWeek: number
  newBalance: number
  previousBalance: number
  weeklyRank?: number
  overallRank?: number
  weeklyRankChange?: number | null
  overallRankChange?: number | null
  sources: Array<{ name: string; points: number }>
}

export interface PointsLedgerMetadata {
  epoch: string
  weekPointsDistributed: number // Points distributed this week
  totalPointsDistributed: number // Cumulative total points ever distributed
  uniqueAddresses: number
  sources: Array<{ name: string; totalPoints: number }>
  generatedAt: number
  status: string
}

export interface PointsLedgerResponse {
  epoch: string
  metadata: PointsLedgerMetadata | null
  entries: PointsLedgerEntry[]
  lastKey?: string
}

export interface PointsAddressHistoryResponse {
  entries: PointsLedgerEntry[]
  lastKey?: string
}

// Fetch latest ledger (or specific epoch)
export const fetchPointsLedger = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  epoch?: string,
  address?: string
): Promise<PointsLedgerResponse> => {
  try {
    const path = epoch ? `/points/ledger/${epoch}` : '/points/ledger'
    const query = address ? `?address=${encodeURIComponent(address.toLowerCase())}` : ''
    const r = await axios.get(`${serverApiBaseUrl}${path}${query}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    return r.data
  } catch (e) {
    logOT.error('fetchPointsLedger error', e)
    throw e
  }
}

// Fetch address history
export const fetchPointsAddressHistory = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  address: string,
  limit?: number,
  lastKey?: string
): Promise<PointsAddressHistoryResponse> => {
  try {
    let queryString = ''
    if (limit) queryString += `?limit=${limit}`
    if (lastKey) queryString += `${queryString ? '&' : '?'}lastKey=${encodeURIComponent(lastKey)}`

    const r = await axios.get(`${serverApiBaseUrl}/points/address/${address}/history${queryString}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    return r.data
  } catch (e) {
    logOT.error('fetchPointsAddressHistory error', e)
    throw e
  }
}

// Fetch activities (current or all including expired)
export const fetchActivities = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  includeExpired: boolean = false
): Promise<ActivitiesResponse> => {
  try {
    const queryString = includeExpired ? '?includeExpired=true' : ''
    const r = await axios.get(`${serverApiBaseUrl}/points/activities${queryString}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    return r.data
  } catch (e) {
    logOT.error('fetchActivities error', e)
    throw e
  }
}
export enum LytHistoryPeriod {
  OneMonth = '1M',
  ThreeMonths = '3M',
  SixMonths = '6M',
  OneYear = '1Y',
  All = 'All',
}

export const fetchLytHistory = async (
  timerPeriod: LytHistoryPeriod,
  serverApiBaseUrl?: string,
  restAPIKey?: string,
  poolAddr?: string,
  creationTimestamp?: number,
  providerReadOnly?: JsonRpcProvider,
  showAPY: boolean = true
): Promise<LytHistoryCTO[]> => {
  let timestampDelta = 0
  if (providerReadOnly) {
    timestampDelta = await fetchChainTimestampDelta(providerReadOnly)
  }
  const now = new Date(Date.now() + timestampDelta * 1000)

  if (serverApiBaseUrl && restAPIKey && poolAddr) {
    try {
      let start: Date | undefined = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      switch (timerPeriod) {
        case LytHistoryPeriod.OneMonth:
          start.setUTCMonth(start.getUTCMonth() - 1)
          break
        case LytHistoryPeriod.ThreeMonths:
          start.setUTCMonth(start.getUTCMonth() - 3)
          break
        case LytHistoryPeriod.SixMonths:
          start.setUTCMonth(start.getUTCMonth() - 6)
          break
        case LytHistoryPeriod.OneYear:
          start.setUTCFullYear(start.getUTCFullYear() - 1)
          break
        case LytHistoryPeriod.All:
          start = undefined
          break
        default:
          start.setUTCMonth(start.getUTCMonth() - 1)
      }
      if (start && creationTimestamp && start.getTime() < creationTimestamp * 1000) {
        start = undefined
      }
      const startDayStr = start ? start.toISOString().slice(0, 10) : undefined
      const endDayStr = undefined

      logOT.info('fetchLytHistory calling API', {
        serverApiBaseUrl,
        poolAddr,
        startDayStr,
        endDayStr,
        timerPeriod,
        showAPY,
      })

      const result = await fetchDailyPoolOverviewCTOs(serverApiBaseUrl, restAPIKey, poolAddr, startDayStr, endDayStr)

      logOT.info('fetchLytHistory API response', {
        resultCount: result.length,
        showAPY,
      })

      const mappedData = result
        .map((d) => {
          const dynamic = d as any as {
            timestamp: number
            interestRate?: string | number
            exchangeRate?: string | number
          }
          const hasData = showAPY ? dynamic.interestRate !== undefined : dynamic.exchangeRate !== undefined

          return {
            timestamp: dynamic.timestamp,
            value: showAPY
              ? dynamic.interestRate !== undefined
                ? Number(dynamic.interestRate) / 100
                : null
              : dynamic.exchangeRate !== undefined
                ? Number(dynamic.exchangeRate) / 1e18
                : null,
            hasData,
          }
        })
        .filter((item) => item.timestamp && item.hasData && item.value !== null)

      logOT.info('fetchLytHistory filtered data', {
        originalCount: result.length,
        filteredCount: mappedData.length,
        showAPY,
        sampleData: mappedData.slice(0, 3),
      })

      return mappedData.map((item) => ({
        timestamp: item.timestamp,
        value: item.value as number,
      }))
    } catch (error) {
      logOT.error('fetchLytHistory error', error)
      return []
    }
  }
  return []
}

export const fetchReserveActionPlanStateDataCTO = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  lytPoolAddr: EthAddr
): Promise<ReserveActionPlanStateDataCTO> => {
  try {
    logOT.info('fetchLytReservePlan', { serverApiBaseUrl, restAPIKey, lytPoolAddr })
    const r = await axios.get(`${serverApiBaseUrl}/lytReservePlanData/${lytPoolAddr}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': restAPIKey,
      },
    })

    const data = r.data
    logOT.BANNER('fetchLytReservePlan', data)
    if (!data.result) {
      logOT.error('fetchLytPoolReserve error no result')
      throw new Error('No ReserveActionPlanCTO result')
    }

    const reserveActionPlanCTO: ReserveActionPlanStateDataCTO = data.result
    return reserveActionPlanCTO
  } catch (e) {
    logOT.error('fetchLytPoolReserve error', e)
    throw e
  }
}

export const fetchLytExchangeRateCheckCTO = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  lytPoolAddr: EthAddr
): Promise<LytExchangeRateCheckCTO> => {
  const r = await axios.get(`${serverApiBaseUrl}/lytExchangeRateCheck/${lytPoolAddr}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': restAPIKey,
    },
  })
  const data = r.data
  return data.result as LytExchangeRateCheckCTO
}
